// Il fuso orario è un parametro obbligatorio (spec §14, piano M1a D12): è quello dell'azienda, che il
// contesto della richiesta passa come `{ timeZone, weeklyHours, closedDates }`. Nessuna ricaduta su Europe/Rome:
// senza fuso le funzioni lanciano `RangeError`.
import { dayString, MISSING_TIME_ZONE, padNumber, requireTimeZone } from "./local-days.js";
const HOUR_IN_MILLISECONDS = 60 * 60 * 1000;
const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const DEFAULT_WEEKLY_HOURS = Object.freeze({
    1: Object.freeze({ start: "08:30", end: "18:00", endInclusive: true }),
    2: Object.freeze({ start: "08:30", end: "18:00", endInclusive: true }),
    3: Object.freeze({ start: "08:30", end: "18:00", endInclusive: true }),
    4: Object.freeze({ start: "08:30", end: "18:00", endInclusive: true }),
    // 08:30–18:00 anche il venerdì (decisione del product owner del 28 settembre 2026, prima chiudeva alle 15:30).
    5: Object.freeze({ start: "08:30", end: "18:00", endInclusive: true }),
    6: null,
    7: null,
});
const formatterCache = new Map();
function getFormatter(timeZone) {
    let formatter = formatterCache.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hourCycle: "h23",
        });
        formatterCache.set(timeZone, formatter);
    }
    return formatter;
}
function timeKey(date, includeSeconds = false) {
    const time = `${padNumber(date.hour)}:${padNumber(date.minute)}`;
    return includeSeconds ? `${time}:${padNumber(date.second)}` : time;
}
/** Il giorno della settimana ISO di un giorno di calendario: lunedì 1, domenica 7. */
export function isoWeekday(date) {
    const day = new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
    return (day === 0 ? 7 : day);
}
/** Giorno e ora di un istante nel fuso indicato (la sequenza di chiamate li usa per le sue mezze giornate). */
export function zonedPartsOf(instant, timeZone) {
    const values = {};
    for (const part of getFormatter(timeZone).formatToParts(instant)) {
        if (part.type !== "literal")
            values[part.type] = part.value;
    }
    return {
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
        hour: Number(values.hour),
        minute: Number(values.minute),
        second: Number(values.second),
        millisecond: instant.getUTCMilliseconds(),
    };
}
function addCalendarDays(date, amount) {
    const next = new Date(Date.UTC(date.year, date.month - 1, date.day + amount));
    return {
        year: next.getUTCFullYear(),
        month: next.getUTCMonth() + 1,
        day: next.getUTCDate(),
    };
}
function parseLocalDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
    if (!match)
        throw new TypeError("localDate must use YYYY-MM-DD format");
    const date = {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
    };
    const normalized = new Date(Date.UTC(date.year, date.month - 1, date.day));
    if (normalized.getUTCFullYear() !== date.year
        || normalized.getUTCMonth() + 1 !== date.month
        || normalized.getUTCDate() !== date.day) {
        throw new RangeError("localDate is not a valid calendar date");
    }
    return date;
}
function parseClock(value, label) {
    const match = /^(\d{2}):(\d{2})$/.exec(value ?? "");
    if (!match)
        throw new TypeError(`${label} must use HH:mm format`);
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59)
        throw new RangeError(`${label} is not a valid time`);
    return { hour, minute, totalMinutes: hour * 60 + minute };
}
/** L'istante di un giorno e di un'ora locali nel fuso indicato; un'ora che il cambio dell'ora salta lancia `RangeError`. */
export function instantFromLocal(date, clock, timeZone) {
    const target = {
        year: date.year,
        month: date.month,
        day: date.day,
        hour: clock.hour,
        minute: clock.minute,
        second: clock.second ?? 0,
        millisecond: clock.millisecond ?? 0,
    };
    const targetAsUtc = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, target.second, target.millisecond);
    let candidate = targetAsUtc;
    // Intl does not expose a direct local-time-to-instant conversion. Iterating the
    // observed offset is deterministic for the daytime work slots used here and
    // follows the configured time zone across DST changes.
    for (let attempt = 0; attempt < 6; attempt += 1) {
        const observed = zonedPartsOf(new Date(candidate), timeZone);
        const observedAsUtc = Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute, observed.second, observed.millisecond);
        const correction = targetAsUtc - observedAsUtc;
        if (correction === 0)
            return new Date(candidate);
        candidate += correction;
    }
    throw new RangeError(`The local time does not exist in ${timeZone}`);
}
/**
 * Quanti giorni di calendario si cercano in avanti per trovare un giorno aperto: due settimane più una per ogni
 * chiusura dell'azienda (`closedDatesCount`). Lo stesso limite per il calendario lavorativo, per le mezze giornate della
 * sequenza e per i giorni dei richiami: chi lo cambia lo cambia in un posto solo.
 */
export function searchLimitDays(closedDatesCount = 0) {
    return closedDatesCount + 14;
}
function normalizeConfig(config) {
    // A JavaScript caller may omit the configuration: without it there is no time zone.
    if (config === undefined)
        throw new RangeError(MISSING_TIME_ZONE);
    if (config === null || typeof config !== "object" || Array.isArray(config)) {
        throw new TypeError("config must be an object");
    }
    const timeZone = requireTimeZone(config.timeZone);
    try {
        getFormatter(timeZone).format(new Date(0));
    }
    catch {
        formatterCache.delete(timeZone);
        throw new RangeError(`Invalid time zone: ${timeZone}`);
    }
    const weeklyHours = { ...DEFAULT_WEEKLY_HOURS };
    if (config.weeklyHours !== undefined) {
        if (config.weeklyHours === null || typeof config.weeklyHours !== "object" || Array.isArray(config.weeklyHours)) {
            throw new TypeError("weeklyHours must be an object");
        }
        for (const weekday of ISO_WEEKDAYS) {
            if (!Object.hasOwn(config.weeklyHours, weekday))
                continue;
            const interval = config.weeklyHours[weekday];
            if (interval === null) {
                weeklyHours[weekday] = null;
                continue;
            }
            if (typeof interval !== "object" || interval === undefined || Array.isArray(interval)) {
                throw new TypeError(`weeklyHours[${weekday}] must be an interval or null`);
            }
            const start = parseClock(interval.start, `weeklyHours[${weekday}].start`);
            const end = parseClock(interval.end, `weeklyHours[${weekday}].end`);
            if (end.totalMinutes <= start.totalMinutes) {
                throw new RangeError(`weeklyHours[${weekday}] must end after it starts`);
            }
            weeklyHours[weekday] = {
                start: interval.start,
                end: interval.end,
                endInclusive: interval.endInclusive === true,
            };
        }
    }
    if (!Object.values(weeklyHours).some(Boolean)) {
        throw new RangeError("weeklyHours must contain at least one open day");
    }
    const closedDates = new Set(config.closedDates ?? []);
    for (const value of closedDates) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            throw new TypeError("closedDates entries must use YYYY-MM-DD format");
        }
    }
    return { timeZone, weeklyHours, closedDates };
}
function intervalForDate(date, config) {
    if (config.closedDates.has(dayString(date)))
        return null;
    const interval = config.weeklyHours[isoWeekday(date)];
    if (!interval)
        return null;
    const start = parseClock(interval.start, "working interval start");
    const end = parseClock(interval.end, "working interval end");
    return {
        open: instantFromLocal(date, start, config.timeZone),
        close: instantFromLocal(date, end, config.timeZone),
        endInclusive: interval.endInclusive === true,
    };
}
/**
 * L'apertura e la chiusura del giorno `date` nel calendario dell'azienda, oppure `null` se quel giorno è chiuso (giorno
 * della settimana senza orario o data di chiusura). Il calendario si controlla come nelle altre funzioni.
 */
export function workingWindowOf(date, config) {
    return intervalForDate(date, normalizeConfig(config));
}
/**
 * L'orario del calendario giorno per giorno, senza gli istanti: la funzione data dice di `date` l'apertura e la chiusura in minuti
 * dell'orologio dell'azienda, oppure `null` se quel giorno è chiuso, come `workingWindowOf` (giorno della settimana senza orario o
 * data di chiusura). Il calendario si controlla una volta sola, qui. Per i report di un anno (`reportDaysOf`, `callGridLayoutOf`),
 * che per ogni giorno chiedono soltanto se è aperto e i minuti di apertura e chiusura: gli istanti costano due conversioni del fuso
 * per ognuno. Un orario che il cambio dell'ora salta (a Roma fra le 02:00 e le 03:00 dell'ultima domenica di marzo) vale con i
 * minuti scritti nel calendario, dove `workingWindowOf` lancia `RangeError`.
 */
export function workingMinutesOf(config) {
    const normalized = normalizeConfig(config);
    const minutes = new Map();
    for (const weekday of ISO_WEEKDAYS) {
        const interval = normalized.weeklyHours[weekday];
        if (!interval)
            continue;
        minutes.set(weekday, { open: parseClock(interval.start, "working interval start").totalMinutes, close: parseClock(interval.end, "working interval end").totalMinutes });
    }
    return (date) => {
        if (normalized.closedDates.has(dayString(date)))
            return null;
        const day = minutes.get(isoWeekday(date));
        return day ? { ...day } : null;
    };
}
function nextWorkingOpen(date, config, includeDate) {
    let candidateDate = includeDate ? date : addCalendarDays(date, 1);
    const searchLimit = searchLimitDays(config.closedDates.size);
    for (let index = 0; index < searchLimit; index += 1) {
        const interval = intervalForDate(candidateDate, config);
        if (interval)
            return { ...interval, date: candidateDate };
        candidateDate = addCalendarDays(candidateDate, 1);
    }
    throw new RangeError("No available working slot was found");
}
function normalizeStart(instant, config) {
    const local = zonedPartsOf(instant, config.timeZone);
    const date = { year: local.year, month: local.month, day: local.day };
    const interval = intervalForDate(date, config);
    if (!interval)
        return nextWorkingOpen(date, config, false);
    if (instant < interval.open)
        return { ...interval, date };
    if (instant > interval.close || (instant.getTime() === interval.close.getTime() && !interval.endInclusive)) {
        return nextWorkingOpen(date, config, false);
    }
    return { ...interval, date, current: instant };
}
function asResult(instant, timeZone) {
    const local = zonedPartsOf(instant, timeZone);
    const localDate = dayString(local);
    const localTime = timeKey(local);
    return {
        iso: instant.toISOString(),
        localDate,
        localTime,
        localDateTime: `${localDate}T${timeKey(local, true)}`,
        timeZone,
        weekday: isoWeekday(local),
    };
}
/**
 * Adds effective working hours to an instant. Closed time is skipped and an
 * a result exactly on an exclusive closing boundary is moved to the next
 * opening; the default hours close inclusively at 18:00 Monday through Friday.
 */
export function addWorkingHours(startIso, hours, config) {
    if (typeof startIso !== "string" || startIso.trim() === "") {
        throw new TypeError("startIso must be a non-empty ISO date-time string");
    }
    const start = new Date(startIso);
    if (!Number.isFinite(start.getTime()))
        throw new RangeError("startIso is not a valid date-time");
    if (!Number.isFinite(hours) || hours < 0) {
        throw new RangeError("hours must be a finite non-negative number");
    }
    const normalizedConfig = normalizeConfig(config);
    let slot = normalizeStart(start, normalizedConfig);
    let current = slot.current ?? slot.open;
    let remaining = hours * HOUR_IN_MILLISECONDS;
    while (remaining > 0) {
        const available = slot.close.getTime() - current.getTime();
        if (remaining < available || (remaining === available && slot.endInclusive)) {
            current = new Date(current.getTime() + remaining);
            remaining = 0;
            break;
        }
        remaining -= available;
        slot = nextWorkingOpen(slot.date, normalizedConfig, false);
        current = slot.open;
    }
    // An exclusive close moves the result to the next opening. This also covers a
    // zero-hour request that starts exactly at that boundary because normalizeStart
    // already advances it.
    if (current.getTime() > slot.close.getTime()
        || (current.getTime() === slot.close.getTime() && !slot.endInclusive)) {
        slot = nextWorkingOpen(slot.date, normalizedConfig, false);
        current = slot.open;
    }
    return asResult(current, normalizedConfig.timeZone);
}
/**
 * Resolves a date/time entered by the user into its canonical instant and
 * verifies that it is an allowed callback slot in the configured calendar.
 */
export function resolveLocalCallbackSlot(localDate, localTime, config) {
    const date = parseLocalDate(localDate);
    const clock = parseClock(localTime, "localTime");
    const normalizedConfig = normalizeConfig(config);
    const instant = instantFromLocal(date, clock, normalizedConfig.timeZone);
    const interval = intervalForDate(date, normalizedConfig);
    if (!interval)
        throw new RangeError("The selected date is closed for callbacks");
    if (instant < interval.open
        || instant > interval.close
        || (instant.getTime() === interval.close.getTime() && !interval.endInclusive)) {
        throw new RangeError("The selected time is outside callback working hours");
    }
    return asResult(instant, normalizedConfig.timeZone);
}
//# sourceMappingURL=callback-scheduling.js.map